import classnames from 'classnames';
import React, { forwardRef, ReactElement, Ref } from 'react';
import { NumberFormatValues, NumericFormat, NumericFormatProps } from 'react-number-format';
import { v4 as uuid } from 'uuid';

import { LinkButton } from '@eika/button';
import '@eika/currency-input-style';

import InputError from './InputError';
import InputLabel from './InputLabel';

export interface PrecentInputProps extends NumericFormatProps {
    /**
     * The default change event of Number Format.
     * Returns: {formattedValue: "100 000 kr", value: "100000", floatValue: 100000}  */
    onValueChange?: (values: NumberFormatValues) => void;

    /**
     * Fake onChange event to handle external libs, use this only when needed.
     */
    onChange?: (e: { type: string; target: { name: string; value: string | number } }) => void;
    /**
     * Fake onClick event linkbutton
     */
    onClickButton?: () => void;
    /**
     * The input value
     */
    value?: string | number;
    /**
     * Default placeholder text will add prefix + '0' +suffix-
     */
    placeholder?: string;
    /**
     * Prefix text to show on the left side of input
     */
    prefix?: string;
    /**
     * Suffix text to show on the left side of input
     */
    suffix?: string;
    /**
     * Defines the number of decimals you want, default is 0.
     */
    decimalScale?: number;
    /**
     * Tells which decimal separator you want, default is ','
     */
    decimalSeparator?: string;
    /**
     * Allows the use of negative values, default is false.
     */
    allowNegative?: boolean;
    /**
     * Adds classnames to input field, default classes are: 'sgw-currency-input' and 'sgw-currency-input__error' when errors.
     */
    className?: string;
    /**
     * Wraps the input field with a label.
     */
    label?: string;
    /**
     * Adds a sublabel above the input.
     */
    sublabel?: string;
    /**
     * Adds a label to linkbutton.
     */
    buttonlabel?: string;
    /**
     * Adds error output below the input. Also used  to highlight errors by adding the class: 'sgw-currency-input__error' to the input field.
     */
    error?: boolean | string | ReactElement;

    dataTrackId?: string;

    dataTrackLinkname?: string;

    name: string;
}

/* eslint-disable react/jsx-props-no-spreading */
const PrecentInputProps = forwardRef(
    (
        {
            className,
            label,
            sublabel,
            buttonlabel,
            value,
            error,
            onValueChange,
            onClickButton,
            prefix = '',
            suffix = ' %',
            decimalScale = 2,
            decimalSeparator = ',',
            allowNegative = false,
            dataTrackId = label,
            dataTrackLinkname = label,
            ...rest
        }: PrecentInputProps,
        ref: Ref<HTMLInputElement>,
    ) => {
        const id = uuid();

        return (
            <>
                <InputLabel label={label} sublabel={sublabel}>
                    <div
                        className={classnames(
                            'sgw-currency-input',
                            { 'sgw-currency-input__error': !!error },
                            className,
                        )}
                    >
                        <div>
                            <NumericFormat
                                id={id}
                                aria-invalid={!!error}
                                placeholder={`0 ${suffix}`}
                                prefix={prefix}
                                suffix={suffix}
                                decimalScale={decimalScale}
                                decimalSeparator={decimalSeparator}
                                allowNegative={allowNegative}
                                getInputRef={ref}
                                value={value}
                                valueIsNumericString
                                onValueChange={onValueChange}
                                className="max-width-100 margin-right-medium-large sgw-currency-input--input"
                                {...rest}
                            />
                            <LinkButton
                                onClick={onClickButton}
                                data-track-id={dataTrackId}
                                data-track-linkname={dataTrackLinkname}
                            >
                                {buttonlabel}
                            </LinkButton>
                        </div>
                    </div>
                </InputLabel>
                {error && typeof error !== 'boolean' && <InputError error={error} />}
            </>
        );
    },
);

export default PrecentInputProps;
